import { computed, ref } from 'vue'

import { defineStore } from 'pinia'
import { projectService } from '@/database/services'
import type { Project } from '@/types/project'

export type { Project }

export const useProjectStore = defineStore('project', () => {
  // State
  const projects = ref<Project[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)
  const currentProjectId = ref<string | null>(null)
  const currentProjectData = ref<Project | null>(null)

  // Getters
  const getProjects = computed(() => projects.value)
  const getLoading = computed(() => loading.value)
  const getError = computed(() => error.value)
  const currentProject = computed(() => {
    if (!currentProjectId.value) return null
    // Prefer explicit data set by ProjectDetail
    if (currentProjectData.value?.id === currentProjectId.value) return currentProjectData.value
    // Fallback: look up from projects list
    const list = Array.isArray(projects.value) ? projects.value : []
    return list.find((p) => p.id === currentProjectId.value) || null
  })

  // Actions
  const fetchProjects = async (filters: Record<string, any> = {}) => {
    try {
      loading.value = true
      error.value = null
      const data = await projectService.getAll(filters)
      projects.value = data
      return data
    } catch (err) {
      error.value = err.message
      console.error('Error fetching projects:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const fetchProjectById = async (id) => {
    try {
      loading.value = true
      error.value = null
      const data = await projectService.getById(id)
      return data
    } catch (err) {
      error.value = err.message
      console.error('Error fetching project:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const createProject = async (projectData) => {
    try {
      loading.value = true
      error.value = null
      const data = await projectService.create(projectData)
      projects.value.push(data)
      return data
    } catch (err) {
      error.value = err.message
      console.error('Error creating project:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const updateProject = async (id, projectData) => {
    try {
      loading.value = true
      error.value = null
      const data = await projectService.update(id, projectData)
      const index = projects.value.findIndex(p => p.id === id)
      if (index !== -1) {
        projects.value[index] = data
      }
      return data
    } catch (err) {
      error.value = err.message
      console.error('Error updating project:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const deleteProject = async (id) => {
    try {
      loading.value = true
      error.value = null
      await projectService.delete(id)
      projects.value = projects.value.filter(p => p.id !== id)
      return true
    } catch (err) {
      error.value = err.message
      console.error('Error deleting project:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const fetchProjectsByTeam = async (teamId) => {
    try {
      loading.value = true
      error.value = null
      const data = await projectService.getByTeam(teamId)
      return data
    } catch (err) {
      error.value = err.message
      console.error('Error fetching projects by team:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const fetchProjectsByStatus = async (status) => {
    try {
      loading.value = true
      error.value = null
      const data = await projectService.getByStatus(status)
      return data
    } catch (err) {
      error.value = err.message
      console.error('Error fetching projects by status:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const getProjectStats = async (projectId) => {
    try {
      loading.value = true
      error.value = null
      const data = await projectService.getStatistics(projectId)
      return data
    } catch (err) {
      error.value = err.message
      console.error('Error fetching project stats:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const clearError = () => {
    error.value = null
  }

  const clearProjects = () => {
    projects.value = []
  }

  const setCurrentProject = (id: string | null, data?: Project | null) => {
    currentProjectId.value = id
    currentProjectData.value = data || null
  }

  const clearCurrentProject = () => {
    currentProjectId.value = null
    currentProjectData.value = null
  }

  // Convenience methods that match what views expect
  const getProjectsFunction = async (filters = {}) => {
    return await fetchProjects(filters)
  }

  const getProject = async (id) => {
    return await fetchProjectById(id)
  }

  return {
    // State
    projects,
    loading,
    error,
    // Getters (computed)
    projectsList: getProjects, // computed getter renamed to avoid conflict
    getLoading,
    getError,
    // Actions
    fetchProjects,
    fetchProjectById,
    createProject,
    updateProject,
    deleteProject,
    fetchProjectsByTeam,
    fetchProjectsByStatus,
    getProjectStats,
    clearError,
    clearProjects,
    // Current project
    currentProjectId,
    currentProject,
    setCurrentProject,
    clearCurrentProject,
    // Convenience methods - function versions for backwards compatibility
    getProjects: getProjectsFunction, // Function version for components expecting it
    getProjectById: getProject
  }
})
